import { LitElement, TemplateResult, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { Constructor } from '../types/common/types';
import { SelectOption } from '../ui/gzSelect/types';
import { elementClassListRemove, elementClassListToggle } from '../utils/htmlElement.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { isEqual } from 'lodash';

export enum InlineMenuEvents {
  ACTIVATE = 'activateInlineMenu',
}

// A mixin created for LitElement components
// https://lit.dev/docs/composition/mixins/#mixins-in-typescript
declare class InlineMenuMixinInterface {
  inlineActions: Array<SelectOption>;
  itemData: unknown;
  selectDisabled: boolean;
  renderInlineMenu(): TemplateResult | undefined;
}

export const InlineMenuMixin = <SuperClass extends Constructor<LitElement>>(superClass: SuperClass) => {
  class ElementHasInlineMenu extends superClass {
    @property({ type: Array, reflect: true })
    inlineActions: Array<SelectOption>;

    @property({
      attribute: false,
      hasChanged(value: unknown, oldValue: unknown): boolean {
        return !isEqual(value, oldValue);
      },
    })
    itemData: unknown;

    @property({ type: Boolean, attribute: true, reflect: true })
    selectDisabled = false;

    @query('.inline-menu-trigger')
    menuTrigger: HTMLElement;

    private handleSelectionChange(e: CustomEvent) {
      const eventName = e.detail.selected;
      this.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            itemData: this.itemData,
          },
          bubbles: true,
          composed: true,
        })
      );
    }

    private handleMouseDown(event) {
      elementClassListToggle(this, 'menu-open');
      //add active class only if left button on mouse is clicked
      if (event.button === 0) {
        elementClassListToggle(this.menuTrigger, 'active');
        this.dispatchEvent(
          new CustomEvent(InlineMenuEvents.ACTIVATE, {
            detail: {
              itemData: this.itemData,
            },
            bubbles: true,
            composed: true,
          })
        );
      }
    }

    private handleOverlayClosed() {
      elementClassListRemove(this, 'menu-open');
      if (this.menuTrigger) {
        elementClassListRemove(this.menuTrigger, 'active');
        this.menuTrigger.blur();
      }
    }

    renderInlineMenu(): TemplateResult | undefined {
      if (!isEmpty(this.inlineActions) && this.inlineActions && this.inlineActions.length) {
        return html`
          <gz-select
            .options="${this.inlineActions}"
            class="inline-menu"
            closeOnMouseLeave
            @overlayClosed="${this.handleOverlayClosed}"
            @selectionChanged="${this.handleSelectionChange}"
            ?disabled="${this.selectDisabled}"
          >
            <gz-icon-button
              icon="ds-icon-more"
              buttonColor="secondary"
              buttonType="square"
              fontSize="22"
              class="inline-menu-trigger"
              @mousedown="${this.handleMouseDown}"
            ></gz-icon-button>
          </gz-select>
        `;
      }
    }
  }

  return ElementHasInlineMenu as Constructor<InlineMenuMixinInterface> & SuperClass;
};
